/**
 * VisitorAPI (visitorapi.com) — геолокація та метадані пристрою для поточного відвідувача.
 * Виклик через офіційний пакет `visitorapi` (див. https://visitorapi.com/docs/react).
 * Поля відповіді: https://visitorapi.com/docs/visitor-data
 *
 * Для рядків у адмінці (інші користувачі) очікується знімок у відповіді бекенду в полі `visitor`.
 */

import VisitorAPI from "visitorapi";

export type VisitorApiData = {
  ipAddress?: string;
  countryCode?: string;
  countryName?: string;
  currencies?: string[];
  languages?: string[];
  region?: string;
  city?: string;
  cityLatLong?: string;
  browser?: string;
  browserVersion?: string;
  deviceBrand?: string;
  deviceModel?: string;
  deviceFamily?: string;
  os?: string;
  osVersion?: string;
};

export function flagEmojiFromIso3166Alpha2(code: string | undefined): string {
  if (!code || code.length !== 2) return "";
  const u = code.toUpperCase();
  if (!/^[A-Z]{2}$/.test(u)) return "";
  return String.fromCodePoint(
    127397 + u.charCodeAt(0),
    127397 + u.charCodeAt(1)
  );
}

function pick(raw: Record<string, unknown>, ...keys: string[]): unknown {
  for (const k of keys) {
    const v = raw[k];
    if (v !== undefined && v !== null && v !== "") return v;
  }
  return undefined;
}

export function normalizeVisitorApiPayload(raw: unknown): VisitorApiData | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  const source = raw as Record<string, unknown>;
  const nestedData =
    source.data && typeof source.data === "object"
      ? (source.data as Record<string, unknown>)
      : undefined;
  const hasDirectVisitorFields =
    pick(
      source,
      "ipAddress",
      "countryCode",
      "countryName",
      "currencies",
      "languages",
      "region",
      "city",
      "cityLatLong",
      "browser",
      "browserVersion",
      "deviceBrand",
      "deviceModel",
      "deviceFamily",
      "os",
      "osVersion"
    ) !== undefined;
  const o = hasDirectVisitorFields || !nestedData ? source : nestedData;
  const currencies = pick(o, "currencies", "currencyCodes");
  const languages = pick(o, "languages", "languageCodes");
  const out: VisitorApiData = {
    ipAddress: pick(o, "ipAddress", "ip_address", "ip") as string | undefined,
    countryCode: pick(o, "countryCode", "country_code") as string | undefined,
    countryName: pick(o, "countryName", "country_name") as string | undefined,
    currencies: Array.isArray(currencies)
      ? (currencies as string[])
      : currencies
      ? [String(currencies)]
      : undefined,
    languages: Array.isArray(languages)
      ? (languages as string[])
      : languages
      ? [String(languages)]
      : undefined,
    region: pick(o, "region", "regionCode", "region_code") as string | undefined,
    city: pick(o, "city") as string | undefined,
    cityLatLong: pick(o, "cityLatLong", "city_lat_long", "latLong") as
      | string
      | undefined,
    browser: pick(o, "browser") as string | undefined,
    browserVersion: pick(o, "browserVersion", "browser_version") as
      | string
      | undefined,
    deviceBrand: pick(o, "deviceBrand", "device_brand") as string | undefined,
    deviceModel: pick(o, "deviceModel", "device_model") as string | undefined,
    deviceFamily: pick(o, "deviceFamily", "device_family") as string | undefined,
    os: pick(o, "os", "osName", "os_name") as string | undefined,
    osVersion: pick(o, "osVersion", "os_version") as string | undefined,
  };
  const hasAny = Object.values(out).some(
    v => v !== undefined && !(Array.isArray(v) && v.length === 0)
  );
  return hasAny ? out : undefined;
}

export async function fetchVisitorApiData(
  projectId: string
): Promise<VisitorApiData> {
  const data = await VisitorAPI(projectId);
  return (data || {}) as VisitorApiData;
}

export function initGoogleTagManager(containerId: string): void {
  if (typeof window === "undefined" || !containerId) return;
  if (document.querySelector(`script[data-gtm-id="${containerId}"]`)) return;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    "gtm.start": Date.now(),
    event: "gtm.js",
  });

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(
    containerId
  )}`;
  script.dataset.gtmId = containerId;
  document.head.appendChild(script);

  const iframe = document.createElement("iframe");
  iframe.src = `https://www.googletagmanager.com/ns.html?id=${encodeURIComponent(
    containerId
  )}`;
  iframe.height = "0";
  iframe.width = "0";
  iframe.style.display = "none";
  iframe.style.visibility = "hidden";
  const nos = document.createElement("noscript");
  nos.appendChild(iframe);
  document.body.insertBefore(nos, document.body.firstChild);
}
